import { z } from 'zod';
import { type SignalKey, signalKeys } from '../devices/contracts';

export const recordKinds = ['organization', 'site', 'device'] as const;
export const osValues = ['windows', 'macos', 'linux', 'unknown'] as const;
export const statusValues = ['healthy', 'attention', 'offline', 'unknown'] as const;
/** A device's role (WRMM-255): a server when its chassis is one or its OS is a server edition, else a workstation. */
export const roleValues = ['server', 'workstation'] as const;
export const sortFields = ['relevance', 'title', 'status', 'lastSeenAt', 'updatedAt'] as const;

const ids = z.array(z.uuid()).min(1).max(20);
const clause = z.discriminatedUnion('field', [
  z.object({ field: z.literal('organizationId'), op: z.literal('in'), values: ids }).strict(),
  z.object({ field: z.literal('siteId'), op: z.literal('in'), values: ids }).strict(),
  z.object({ field: z.literal('os'), op: z.literal('in'), values: z.array(z.enum(osValues)).min(1).max(4) }).strict(),
  z.object({ field: z.literal('status'), op: z.literal('in'), values: z.array(z.enum(statusValues)).min(1).max(4) }).strict(),
  z.object({ field: z.literal('role'), op: z.literal('in'), values: z.array(z.enum(roleValues)).min(1).max(2) }).strict(),
  z.discriminatedUnion('op', [
    z.object({ field: z.literal('lastSeenAt'), op: z.enum(['before', 'after']), value: z.iso.datetime({ offset: true }) }).strict(),
    // A device that has never reported. Date comparisons never match it.
    z.object({ field: z.literal('lastSeenAt'), op: z.literal('unknown') }).strict(),
  ]),
  // Devices with any of these open health signals.
  z.object({ field: z.literal('signal'), op: z.literal('in'), values: z.array(z.enum(signalKeys)).min(1).max(signalKeys.length) }).strict(),
  // Devices with installed software whose name contains this text.
  z.object({ field: z.literal('software'), op: z.literal('contains'), value: z.string().trim().min(2).max(100) }).strict(),
  z.object({ field: z.literal('duplicateName'), op: z.literal('is'), value: z.literal(true) }).strict(),
]);

// Visual filters and proposed AI queries must use this same bounded contract.
export const searchQuerySchema = z.object({
  version: z.literal(1).default(1), q: z.string().trim().max(200).default(''),
  kinds: z.array(z.enum(recordKinds)).min(1).max(3)
    .refine(values => new Set(values).size === values.length, 'Duplicate record kinds').default([...recordKinds]),
  // One group per visual filter field (customer, site, OS, status, check-in, signal, software, duplicate name).
  filters: z.array(z.object({ mode: z.enum(['and', 'or']), clauses: z.array(clause).min(1).max(8) }).strict()).max(8).default([]),
  // Relevance ignores direction; status ascending means most severe first.
  sort: z.object({ field: z.enum(sortFields), direction: z.enum(['asc', 'desc']) }).strict()
    .default({ field: 'relevance', direction: 'desc' }),
  limit: z.number().int().min(1).max(100).default(25), offset: z.number().int().min(0).max(10000).default(0),
}).strict().superRefine((query, ctx) => {
  const fields = query.filters.flatMap(group => group.clauses.map(c => c.field));
  if (fields.some(f => ['os', 'status', 'role', 'lastSeenAt', 'signal', 'software', 'duplicateName'].includes(f)) && query.kinds.some(k => k !== 'device')) {
    ctx.addIssue({ code: 'custom', message: 'Device filters require kinds: [device]' });
  }
  if (fields.includes('siteId') && query.kinds.includes('organization')) {
    ctx.addIssue({ code: 'custom', message: 'Site filters support site and device records only' });
  }
  if (['status', 'lastSeenAt'].includes(query.sort.field) && query.kinds.some(k => k !== 'device')) {
    ctx.addIssue({ code: 'custom', message: 'Device sorting requires kinds: [device]' });
  }
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;
export type SearchQueryInput = z.input<typeof searchQuerySchema>;
export type SearchClause = z.infer<typeof clause>;
export type RecordKind = typeof recordKinds[number];
export type OsValue = typeof osValues[number];
export type StatusValue = typeof statusValues[number];
export type FilterField = SearchClause['field'];

export interface SearchHit {
  kind: RecordKind; id: string; title: string;
  /** Device identity: title prefers the owner's display name; hostname remains agent-reported. */
  hostname?: string; displayName?: string | null; supportId?: string;
  organizationId: string; siteId: string | null; os: string | null; status: string | null;
  /** Devices only: whether being offline needs a look (servers) or is just a status (workstations, WRMM-255). */
  role: (typeof roleValues)[number] | null;
  lastSeenAt: string | null; serial: string | null;
  /** Active devices with this case-insensitive hostname in the same customer, within the caller's row security.
   * Computed before search/filter/pagination. Optional for older cached hits. Never an authorization identifier. */
  sameNameCount?: number;
  // Parent names are read under the same row security; null when not applicable.
  organizationName: string | null; siteName: string | null;
  /** Devices only: whether an enrolled agent reports for it, and its open signals, most severe first. */
  managed: boolean | null; signals: Array<{ key: SignalKey; severity: 'critical' | 'warning' | 'info'; title: string }>;
  source: { system: 'wittco'; updatedAt: string; recordPath: string };
}
export interface SearchResult { query: SearchQuery; total: number; hits: SearchHit[] }

// Each facet ignores top-level filter groups that only constrain its own field, so
// a multi-select shows counts for the values it could add. Other groups still apply.
export interface SearchFacets {
  query: SearchQuery; computedAt: string; total: number;
  kinds: Partial<Record<RecordKind, number>>;
  os: Record<OsValue, number>; status: Record<StatusValue, number>;
  // Device check-in age relative to computedAt: under 1 day, 1-7 days, older, never.
  lastSeen: { day: number; week: number; older: number; unknown: number };
  organizations: Array<{ id: string; title: string; count: number }>;
  sites: Array<{ id: string; title: string; organizationId: string; count: number }>;
  /** Devices with each open signal (a device can count under several). */
  signals: Partial<Record<SignalKey, number>>;
  /** Devices with and without an enrolled agent. */
  agent: { managed: number; unmanaged: number };
  /** Matched devices that are offline servers: the only offline devices that need a look (WRMM-255). */
  offlineServers: number;
}

export interface SessionMembership {
  mspId: string; mspName: string | null; role: string;
  capabilities: Array<'devices.enroll' | 'diagnostics.run' | 'scripts.run' | 'jobs.approve' | 'remote.connect' | 'end_users.manage'>;
  organizationId: string | null; organizationName: string | null;
  siteId: string | null; siteName: string | null;
}
/** `synthetic` means the API serves invented demo records; the console must say so wherever it shows them. */
export interface SessionInfo {
  /** Native support desk is available on this server. */
  serviceDesk?: boolean; personId: string; expiresAt: string; memberships: SessionMembership[]; approvalKeys: number;
  /** The person's other second factors (WRMM-134): an authenticator app, and recovery codes not yet used. */
  authenticator: boolean; recoveryCodes: number;
  /** A customer user's own customer and MSP (WRMM-134; WRMM-59): they have the portal, never the technician console. */
  customerUser: { organizationName: string; mspName: string } | null;
  /** The MSPs where this person manages people: its owners and admins (WRMM-133). */
  managesPeopleIn: string[];
  dataset: 'synthetic' | 'live';
  /** Whether this server has WITTCO Remote, so devices can offer remote sessions. */
  remoteAccess?: boolean }

export interface EvidenceBundle {
  version: 1; mode: 'read-only'; mspId: string; retrievedAt: string;
  result: SearchResult; contentTrust: 'untrusted-record-content';
  limitations: string[];
}
